import httpservice from "./httpservice";

/**
 * Normalize an optional enum query param.
 * The logs API accepts a single value (not a CSV / array).
 * @param {string|string[]|null|undefined} value
 * @returns {string|undefined}
 */
function toSingleEnum(value) {
  if (value == null || value === "" || value === "ALL") return undefined;
  if (Array.isArray(value)) return value.length === 1 ? String(value[0]).trim() || undefined : undefined;
  const normalized = String(value).trim();
  return normalized && normalized !== "ALL" ? normalized : undefined;
}

/**
 * Build query params for the admin API request-log endpoints.
 * Mirrors the conventions used in organizationservice: only CUSTOM
 * presets carry dateFrom/dateTo; all other presets are datePreset alone.
 */
function buildApiRequestLogQuery(params = {}) {
  const query = {};
  if (params?.page) query.page = params.page;
  if (params?.limit) query.limit = params.limit;
  if (params?.search) query.search = params.search;
  if (params?.sortBy) query.sortBy = params.sortBy;
  if (params?.sortOrder) query.sortOrder = params.sortOrder;
  if (params?.datePreset) query.datePreset = params.datePreset;
  if (params?.datePreset === "CUSTOM") {
    if (params?.dateFrom) query.dateFrom = params.dateFrom;
    if (params?.dateTo) query.dateTo = params.dateTo;
  }

  const status = toSingleEnum(params?.status);
  if (status) query.status = status;

  const statusClass = toSingleEnum(params?.statusClass);
  if (statusClass) query.statusClass = statusClass;

  const method = toSingleEnum(params?.method);
  if (method) query.method = method;

  const failureCategory = toSingleEnum(params?.failureCategory);
  if (failureCategory) query.failureCategory = failureCategory;

  return query;
}

class TransmissionService {
  /**
   * GET /admin/cp/organizations/invoice-trends
   * Params must follow period rules (see buildInvoiceTrendsQuery).
   * Expected periods: YEARLY | MONTHLY | WEEKLY | DAILY
   */
  trends(params = {}) {
    const query = { organizationId: params.organizationId, period: params.period };

    if (params.period === "MONTHLY" && params.year) {
      query.year = String(params.year);
    }
    if (params.period === "WEEKLY") {
      query.year = String(params.year);
      query.month = String(params.month);
    }
    if (params.period === "DAILY") {
      query.year = String(params.year);
      query.month = String(params.month);
      query.week = String(params.week);
    }

    return httpservice.get(`/admin/cp/organizations/invoice-trends`, { params: query });
  }

  trendsSummary(params = {}) {
    const { organizationId, ...rest } = params;
    return httpservice.get(`/admin/cp/organizations/${organizationId}/transmission-count-summary`, { params: rest });
  }

  getTrendsCountAdmin(params = {}) {
    return httpservice.get(`/admin/cp/organizations/${params.organizationId}/trends-count`, { params });
  }

  getLogs(params = {}) {
    return httpservice.get(`/admin/cp/organizations/${params.organizationId}/request-logs`, { params });
  }

  getLog({ organizationId, logId, integrationId } = {}) {
    return httpservice.get(`/admin/cp/organizations/${organizationId}/api-request-logs/${logId}`, {
      params: integrationId ? { integrationId } : undefined,
    });
  }

  /**
   * GET /admin/cp/organizations/:orgId/api-request-logs
   * Paginated API request log list.
   * Params: datePreset, dateFrom/dateTo (CUSTOM only), page, limit,
   * search, status (SUCCESS|FAILED), statusClass (2xx|3xx|4xx|5xx),
   * method (GET|POST|PATCH), failureCategory (VALIDATION|UPSTREAM),
   * sortBy, sortOrder.
   */
  getApiRequestLogs(params = {}) {
    const { organizationId } = params;
    return httpservice.get(`/admin/cp/organizations/${organizationId}/api-request-logs`, {
      params: buildApiRequestLogQuery(params),
    });
  }

  /**
   * GET /admin/cp/organizations/:orgId/api-request-logs/summary
   * Aggregate success/error/invoice transmission summary.
   * Params: datePreset, dateFrom/dateTo (CUSTOM only).
   */
  getApiRequestLogsSummary(params = {}) {
    const { organizationId } = params;
    return httpservice.get(`/admin/cp/organizations/${organizationId}/api-request-logs/summary`, {
      params: buildApiRequestLogQuery(params),
    });
  }

  /**
   * GET /admin/cp/organizations/:orgId/api-request-logs/top-endpoints
   * Request count, latency and error rate grouped by endpoint.
   * Params: datePreset, dateFrom/dateTo (CUSTOM only).
   */
  getApiRequestLogsTopEndpoints(params = {}) {
    const { organizationId } = params;
    return httpservice.get(`/admin/cp/organizations/${organizationId}/api-request-logs/top-endpoints`, {
      params: buildApiRequestLogQuery(params),
    });
  }

  /**
   * GET /admin/cp/organizations/:orgId/api-request-logs/failures-by-category
   * Failure counts broken down by category (VALIDATION, AUTH, TIMEOUT, etc.).
   * Params: datePreset, dateFrom/dateTo (CUSTOM only).
   */
  getApiRequestLogsFailuresByCategory(params = {}) {
    const { organizationId } = params;
    return httpservice.get(`/admin/cp/organizations/${organizationId}/api-request-logs/failures-by-category`, {
      params: buildApiRequestLogQuery(params),
    });
  }

  /**
   * GET /admin/cp/organizations/:orgId/api-request-logs/monthly-usage
   * Monthly API call count series for the selected period.
   * Params: datePreset, dateFrom/dateTo (CUSTOM only).
   */
  getApiRequestLogsMonthlyUsage(params = {}) {
    const { organizationId } = params;
    return httpservice.get(`/admin/cp/organizations/${organizationId}/api-request-logs/monthly-usage`, {
      params: buildApiRequestLogQuery(params),
    });
  }

  /**
   * GET /admin/cp/organizations/:orgId/api-request-logs/webhooks
   * Paginated organization webhook notification log list.
   * Params: datePreset, dateFrom/dateTo (CUSTOM only), page, limit.
   */
  getApiRequestLogsWebhooks(params = {}) {
    const { organizationId } = params;
    return httpservice.get(`/admin/cp/organizations/${organizationId}/api-request-logs/webhooks`, {
      params: buildApiRequestLogQuery(params),
    });
  }

  retrySyncAll(params = {}) {
    localStorage.setItem("action", "retrySyncAll");
    return httpservice.post(`/admin/cp/firs/action-sync-all`, {}, { params });
  }
}

export default new TransmissionService();
